import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Building2, Users, MessageSquare, Send, Trash2, Pencil,
  ArrowLeft, Loader2, Save, ExternalLink, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return format(new Date(iso), "dd MMM yyyy", { locale: es }); }
  catch { return iso; }
}
function relative(iso) {
  if (!iso) return "";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es }); }
  catch { return ""; }
}

export default function AdminOrganizations() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dmOpen, setDmOpen] = useState(null);      // org object
  const [editOpen, setEditOpen] = useState(null);  // org object
  const [detailOpen, setDetailOpen] = useState(null); // org_id

  const { data, isLoading } = useQuery({
    queryKey: ["admin-organizations"],
    queryFn: async () => (await api.get("/admin/organizations")).data,
    refetchInterval: 30_000,
  });

  const deleteMutation = useMutation({
    mutationFn: async (orgId) => (await api.delete(`/admin/organizations/${orgId}`)).data,
    onSuccess: () => {
      toast.success("Organización eliminada");
      qc.invalidateQueries({ queryKey: ["admin-organizations"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const orgs = (data?.organizations || []).filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return o.name?.toLowerCase().includes(s) || o.admin?.email?.toLowerCase().includes(s);
  });

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-orgs-page">
      <button onClick={() => navigate("/admin")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Volver al panel Admin
      </button>

      <header className="glass-panel rounded-3xl p-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-red-500 dark:text-red-300 font-semibold flex items-center gap-2">
            <Building2 className="h-4 w-4" /> Super Admin · Organizaciones
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-semibold mt-1.5">
            {orgs.length} organización(es) corporativa(s)
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Gestiona organizaciones corporativas: seats, estado y comunicación directa con el admin.</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Buscar por nombre o admin…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-[260px]"
            data-testid="admin-orgs-search"
          />
        </div>
      </header>

      <section className="glass-panel rounded-3xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b border-white/10">
            <tr>
              <th className="text-left font-medium py-3 px-4">Organización</th>
              <th className="text-left font-medium py-3 px-4">Admin</th>
              <th className="text-left font-medium py-3 px-4">Plan</th>
              <th className="text-left font-medium py-3 px-4">Miembros</th>
              <th className="text-left font-medium py-3 px-4">Renueva</th>
              <th className="text-right font-medium py-3 px-4">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading ? (
              <tr><td colSpan={6} className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></td></tr>
            ) : orgs.length === 0 ? (
              <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Aún no hay organizaciones corporativas.</td></tr>
            ) : orgs.map((o) => (
              <tr key={o.org_id} data-testid={`admin-org-${o.org_id}`} className="hover:bg-white/40 dark:hover:bg-white/5">
                <td className="py-3 px-4">
                  <div className="font-semibold">{o.name}</div>
                  <div className="text-xs text-muted-foreground">{o.org_id}</div>
                </td>
                <td className="py-3 px-4">
                  <div className="text-sm">{o.admin?.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{o.admin?.email}</div>
                </td>
                <td className="py-3 px-4">
                  <div className="text-sm capitalize">{o.billing_cycle || "—"}</div>
                  <div className="text-xs text-muted-foreground">{o.seats_paid} asientos · {o.currency?.toUpperCase()}</div>
                </td>
                <td className="py-3 px-4">
                  <span className="inline-flex items-center gap-1 bg-blue-500/15 text-blue-700 dark:text-blue-300 rounded-full px-2.5 py-0.5 font-semibold">
                    <Users className="h-3 w-3" /> {o.members_count}
                  </span>
                  {o.pending_invites > 0 && (
                    <span className="ml-1 text-[10px] text-amber-600">+{o.pending_invites} pendientes</span>
                  )}
                </td>
                <td className="py-3 px-4 text-xs text-muted-foreground">{fmtDate(o.current_period_end)}</td>
                <td className="py-3 px-4 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => setDetailOpen(o.org_id)} data-testid={`admin-org-detail-${o.org_id}`} title="Ver detalle">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDmOpen(o)} data-testid={`admin-org-dm-${o.org_id}`} title="Enviar mensaje">
                    <MessageSquare className="h-4 w-4 text-blue-500" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditOpen(o)} data-testid={`admin-org-edit-${o.org_id}`} title="Editar">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-rose-500 hover:text-rose-600"
                    onClick={() => {
                      if (window.confirm(`¿Eliminar la organización "${o.name}"? Los miembros pasan a plan gratuito. Su contenido se conserva.`)) {
                        deleteMutation.mutate(o.org_id);
                      }
                    }}
                    data-testid={`admin-org-delete-${o.org_id}`}
                    title="Eliminar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {editOpen && <EditOrgDialog org={editOpen} onClose={() => setEditOpen(null)} onSaved={() => qc.invalidateQueries({ queryKey: ["admin-organizations"] })} />}
      {dmOpen && <DmDialog org={dmOpen} onClose={() => setDmOpen(null)} />}
      {detailOpen && <DetailDialog orgId={detailOpen} onClose={() => setDetailOpen(null)} />}
    </div>
  );
}

function EditOrgDialog({ org, onClose, onSaved }) {
  const [name, setName] = useState(org.name);
  const [seats, setSeats] = useState(org.seats_paid);
  const [status, setStatus] = useState(org.subscription_status);
  const [slug, setSlug] = useState(org.slug || "");
  const [color, setColor] = useState(org.branding?.primary_color || "#2563eb");
  const fileRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(org.branding?.logo_url || "");

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {};
      if (name !== org.name) body.name = name.trim();
      if (Number(seats) !== org.seats_paid) body.seats_paid = Number(seats);
      if (status !== org.subscription_status) body.subscription_status = status;
      const cleanSlug = (slug || "").toLowerCase().replace(/[^a-z0-9-]/g, "");
      if (cleanSlug !== (org.slug || "")) body.slug = cleanSlug;
      if ((color || "").toLowerCase() !== (org.branding?.primary_color || "").toLowerCase()) body.primary_color = color;
      if (Object.keys(body).length === 0) return null;
      return (await api.patch(`/admin/organizations/${org.org_id}`, body)).data;
    },
    onSuccess: (r) => {
      if (r) { toast.success("Organización actualizada"); onSaved(); onClose(); }
      else { onClose(); }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const uploadLogo = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData(); fd.append("file", file);
      return (await api.post(`/admin/organizations/${org.org_id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })).data;
    },
    onSuccess: (r) => { toast.success("Logo cargado"); setLogoUrl(r.logo_url); onSaved(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="admin-org-edit-dialog">
        <DialogHeader>
          <DialogTitle>Editar organización</DialogTitle>
          <DialogDescription>Solo Super Admin. Los cambios se aplican de inmediato.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 py-2">
          <div className="space-y-1"><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-org-edit-name" /></div>
          <div className="space-y-1"><Label>Asientos pagados</Label><Input type="number" min={3} value={seats} onChange={(e) => setSeats(e.target.value)} data-testid="admin-org-edit-seats" /></div>
          <div className="space-y-1 md:col-span-2"><Label>Estado de suscripción</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="admin-org-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activa</SelectItem>
                <SelectItem value="past_due">Vencida</SelectItem>
                <SelectItem value="canceled">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 border-t border-white/10 pt-3">
            <p className="text-xs uppercase tracking-wider font-semibold text-blue-600 dark:text-blue-300">Marca personalizada</p>
          </div>
          <div className="space-y-1"><Label>Slug (subdominio)</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                   placeholder="empresa-1" maxLength={40} data-testid="admin-org-edit-slug" />
            <p className="text-[10px] text-muted-foreground mt-1">URL: /t/{slug || "..."}</p>
          </div>
          <div className="space-y-1"><Label>Color primario</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                     className="h-9 w-12 rounded cursor-pointer border border-slate-300 dark:border-white/10"
                     data-testid="admin-org-edit-color-picker" />
              <Input value={color} onChange={(e) => setColor(e.target.value)} maxLength={7}
                     className="font-mono uppercase" data-testid="admin-org-edit-color-input" />
            </div>
          </div>
          <div className="space-y-1 md:col-span-2"><Label>Logo</Label>
            <div className="flex items-center gap-3">
              <div className="h-12 w-24 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 grid place-items-center overflow-hidden">
                {logoUrl ? <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-muted-foreground">Sin logo</span>}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                     className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo.mutate(f); e.target.value = ""; }}
                     data-testid="admin-org-edit-logo-file" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploadLogo.isPending} data-testid="admin-org-edit-logo-btn">
                {uploadLogo.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subir logo"}
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="admin-org-edit-save">
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Guardar</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DmDialog({ org, onClose }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-org-dm", org.org_id],
    queryFn: async () => (await api.get(`/admin/organizations/${org.org_id}/messages`)).data,
    refetchInterval: 10_000,
  });
  const [draft, setDraft] = useState("");
  const sendMutation = useMutation({
    mutationFn: async () => (await api.post(`/admin/organizations/${org.org_id}/messages`, { text: draft.trim() })).data,
    onSuccess: () => { setDraft(""); qc.invalidateQueries({ queryKey: ["admin-org-dm", org.org_id] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="admin-org-dm-dialog">
        <DialogHeader>
          <DialogTitle>Mensaje directo con {org.admin?.name || org.admin?.email}</DialogTitle>
          <DialogDescription>El admin de la organización recibirá una notificación.</DialogDescription>
        </DialogHeader>
        <div className="border rounded-2xl bg-muted/30 h-80 overflow-y-auto p-3 space-y-2" data-testid="admin-org-dm-messages">
          {isLoading ? <div className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>
           : (data?.messages || []).length === 0 ? <p className="text-xs text-center text-muted-foreground py-6">Aún no hay mensajes. Envía el primero.</p>
           : (data.messages || []).map(m => (
              <div key={m.message_id} className={cn("flex", m.sender_role === "super_admin" ? "justify-end" : "justify-start")}>
                <div className={cn("max-w-[75%] rounded-xl px-3 py-2 text-sm",
                  m.sender_role === "super_admin" ? "bg-red-600 text-white" : "bg-white dark:bg-white/10")}>
                  <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">
                    {m.sender_role === "super_admin" ? "Tú (Sprintroll)" : m.sender_name}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className="text-[10px] opacity-70 mt-1">{relative(m.created_at)}</div>
                </div>
              </div>
            ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (draft.trim()) sendMutation.mutate(); }} className="flex gap-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (draft.trim()) sendMutation.mutate(); } }}
            placeholder="Escribe un mensaje al admin…"
            rows={2}
            className="resize-none"
            data-testid="admin-org-dm-input"
          />
          <Button type="submit" disabled={!draft.trim() || sendMutation.isPending} data-testid="admin-org-dm-send" className="self-end">
            {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ orgId, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-org-detail", orgId],
    queryFn: async () => (await api.get(`/admin/organizations/${orgId}`)).data,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-org-detail-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{data?.org?.name || "Cargando…"}</DialogTitle>
          <DialogDescription>Detalle interno para Super Admin.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : (
          <div className="text-sm space-y-3">
            <p><span className="text-muted-foreground">Admin:</span> {data.admin?.name} ({data.admin?.email})</p>
            <p><span className="text-muted-foreground">Plan:</span> {data.org.plan_id} — {data.org.seats_paid} seats</p>
            <p><span className="text-muted-foreground">Estado:</span> {data.org.subscription_status}</p>
            <p><span className="text-muted-foreground">Renueva:</span> {fmtDate(data.org.current_period_end)}</p>
            <p><span className="text-muted-foreground">Tableros totales:</span> {data.boards_total}</p>
            <div>
              <p className="text-muted-foreground mb-1">Miembros ({data.members.length}):</p>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {data.members.map(m => (
                  <li key={m.user_id} className="flex items-center gap-2 text-xs bg-muted/30 rounded px-2 py-1.5">
                    <span className="font-medium">{m.name || m.email}</span>
                    <span className="text-muted-foreground">{m.email}</span>
                    {m.org_role === "admin" && <span className="ml-auto text-[10px] bg-blue-500/15 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full uppercase font-semibold">admin</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
